import 'react';
import classnames from 'classnames';
import styled from 'styled-components';
import { selectFromTheme } from '@thisisbud/sc-utils';
import { SuccessNotificationIcon, InfoNotificationIcon, WarningNotificationIcon, ErrorNotificationIcon } from '@thisisbud/bui-react-notification-icons';
import { jsxs, jsx } from 'react/jsx-runtime';

// Info
const colorInfoBackground = '#e4f3fd';
const colorWarningBackground = '#fff6e5';
const colorPositiveBackground = '#e8f7f0';
const colorNegativeBackground = '#ffebeb';

// Padding
const paddingCommonCompact = "0.75rem";

// Margin
const marginCommonCompact = "0.75rem";

let _ = t => t,
  _t;
const notificationClassName = 'bui-notification';
const errorNotificationClassName = `${notificationClassName}--error`;
const warningNotificationClassName = `${notificationClassName}--warning`;
const infoNotificationClassName = `${notificationClassName}--info`;
const successNotificationClassName = `${notificationClassName}--success`;
const notificationIconContainerClassName = `${notificationClassName}__icon-container`;
const messageIconContainerClassName = `${notificationClassName}__message-container`;

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledWrapper = /*#__PURE__*/styled.aside.withConfig({
  displayName: "StyledWrapper",
  componentId: "sc-1fjxfkz-0"
})(_t || (_t = _`
  display: flex;
  flex-direction: row;
  padding: ${0};
  border-radius: ${0};
  color: ${0};
  font-family: ${0};
  font-size: ${0};
  font-weight: ${0};
  line-height: ${0};

  .bui-notification-icon {
    font-size: ${0};
  }

  strong {
    font-weight: ${0};
  }

  &.${0} {
    background-color: ${0};
  }

  &.${0} {
    background-color: ${0};
  }

  &.${0} {
    background-color: ${0};
  }

  &.${0} {
    background-color: ${0};
  }

  .${0} {
    flex-basis: 100%;
    margin-left: ${0};
  }

  * + & {
    margin-top: ${0};
  }

  & + & {
    margin-top: ${0};
  }
`), paddingCommonCompact, selectFromTheme('borderRadiusCardDefault'), selectFromTheme('colorTextPrimaryMain'), selectFromTheme('fontFamilyBodyRegular'), selectFromTheme('fontSizeBodyRegularDefault'), selectFromTheme('fontWeightBodyRegular'), selectFromTheme('lineHeightBodyRegularDefault'), selectFromTheme('fontSizeBodyRegularDefault'), selectFromTheme('fontWeightBodyRegularStrong'), errorNotificationClassName, colorNegativeBackground, warningNotificationClassName, colorWarningBackground, infoNotificationClassName, colorInfoBackground, successNotificationClassName, colorPositiveBackground, messageIconContainerClassName, marginCommonCompact, selectFromTheme('spacingLargeDefault'), selectFromTheme('spacingXXSmallDefault'));

/**
 * Get the class name of the component.
 *
 * @param props - The component props
 */
function getNotificationClassName(type) {
  switch (type) {
    case 'error':
      return classnames(notificationClassName, errorNotificationClassName);
    case 'warning':
      return classnames(notificationClassName, warningNotificationClassName);
    case 'info':
      return classnames(notificationClassName, infoNotificationClassName);
    case 'success':
      return classnames(notificationClassName, successNotificationClassName);
    default:
      return notificationClassName;
  }
}

/**
 * Render the appropriate icon for the notification.
 *
 * @param props - The component props
 */
function renderNotificationIcon(label, type) {
  const computedLabel = `${label}:`;
  switch (type) {
    case 'error':
      return /*#__PURE__*/jsx(ErrorNotificationIcon, {
        label: computedLabel
      });
    case 'warning':
      return /*#__PURE__*/jsx(WarningNotificationIcon, {
        label: computedLabel
      });
    case 'info':
      return /*#__PURE__*/jsx(InfoNotificationIcon, {
        label: computedLabel
      });
    case 'success':
      return /*#__PURE__*/jsx(SuccessNotificationIcon, {
        label: computedLabel
      });
    default:
      throw new Error(`Unsupported notification type: ${type}`);
  }
}

/**
 * Get the role value for the notification based on its type.
 *
 * @param type - The notification type
 * @returns An appropriate role for the notification type
 */
function getNotificationRole(type) {
  switch (type) {
    case 'error':
    case 'success':
      return 'status';
    case 'info':
      return 'note';
    case 'warning':
      return 'strong';
    default:
      return undefined;
  }
}

/**
 * Display a notification to the user.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { Notification } from '@thisisbud/bui-react-notification'
 *
 * function ExampleComponent() {
 *   return <Notification id='example' label='Warning' type='warning'>The quick brown fox jumps over the lazy dog</Notification>
 * }
 * ```
 */
function Notification(props) {
  const {
    as = 'aside',
    children,
    className,
    id,
    label,
    role,
    type = 'info'
  } = props;
  const labelId = `${id}-label`;
  return /*#__PURE__*/jsxs(StyledWrapper, {
    as: as,
    className: classnames(getNotificationClassName(type), className),
    id: id,
    role: typeof role === 'string' ? role : getNotificationRole(type),
    children: [/*#__PURE__*/jsx("div", {
      className: notificationIconContainerClassName,
      id: labelId,
      children: renderNotificationIcon(label, type)
    }), /*#__PURE__*/jsx("div", {
      "aria-labelledby": labelId,
      className: messageIconContainerClassName,
      children: children
    })]
  });
}
Notification.displayName = 'Notification';

export { Notification, errorNotificationClassName, infoNotificationClassName, notificationClassName, successNotificationClassName, warningNotificationClassName };
